import { http } from "./http";

/**
 * IPPBX → Banlist: fail2ban jails on the PBXs, lookup of one address,
 * iplog / userlog output, unban and the ignore list (dbo.PbxIgnoreIp,
 * applied by smx-f2b on every PBX). Admins see everything; managers see the
 * addresses their own PBX logins registered from or that tried their usernames.
 * The API leaves out null fields, hence the optional properties.
 */

export interface ExtensionRef {
  agentId: number;
  agentName: string;
  customerId: number;
  customerName: string;
  pbxUsername: string;
}

export interface TriedUser {
  username: string;
  /** "A registration failed" lines in the FreeSWITCH fail2ban log (this and last week). */
  failed: number;
  /** All registration lines for this username from the address, failed or not. */
  attempts: number;
  lastUtc?: string | null;
  extension?: ExtensionRef | null;
}

export interface BanJail {
  jail: string;
  bantimeSeconds?: number | null;
  /** Banned addresses visible to the caller. */
  banned: number;
}

export interface BanPbxState {
  pbxId: number;
  hostname: string;
  ok: boolean;
  error?: string | null;
  statusUtc?: string | null;
  ipsum: boolean;
  jails: BanJail[];
}

export interface BannedIp {
  pbxId: number;
  hostname: string;
  jail: string;
  ip: string;
  bannedUtc?: string | null;
  endsUtc?: string | null;
  users: TriedUser[];
  /** Usernames of other customers this address tried (managers only see the count). */
  otherUsers: number;
  registeredFrom: ExtensionRef[];
}

export interface Banlist {
  pbxs: BanPbxState[];
  items: BannedIp[];
  isAdmin: boolean;
  sshConfigured: boolean;
  generatedUtc: string;
}

export interface JailHit {
  jail: string;
  bannedUtc?: string | null;
  endsUtc?: string | null;
}

export interface PbxLookup {
  pbxId: number;
  hostname: string;
  ok: boolean;
  error?: string | null;
  jails: JailHit[];
  ipsum: boolean;
  users: TriedUser[];
  otherUsers: number;
}

export interface IgnoreRef {
  pbxIgnoreIpId: number;
  cidr: string;
  comment: string;
  hostname?: string | null;
}

export interface IpLookup {
  ip: string;
  /** False: the address has nothing to do with the manager's extensions; nothing else is filled. */
  inScope: boolean;
  registeredFrom: ExtensionRef[];
  pbxs: PbxLookup[];
  ignored: boolean;
  ignoredBy: IgnoreRef[];
}

export type LogTool = "iplog" | "userlog";

export interface ToolOutput {
  pbxId: number;
  hostname: string;
  tool: LogTool;
  target: string;
  command: string;
  exitCode: number;
  output: string;
  error?: string | null;
  truncated: boolean;
  durationMs: number;
}

export interface Deploy {
  pbxId: number;
  hostname: string;
  ok: boolean;
  skipped: boolean;
  /** not_deployed_yet | in_sync | empty_list */
  skipReason?: string | null;
  error?: string | null;
  entries?: number | null;
  unbanned: string[];
  ipsumRule?: string | null;
}

export interface UnbanPbx {
  pbxId: number;
  hostname: string;
  ok: boolean;
  error?: string | null;
  unbannedFrom: string[];
}

export interface IgnoreEntry {
  pbxIgnoreIpId: number;
  pbxId?: number | null;
  hostname?: string | null;
  cidr: string;
  comment: string;
  isSystem: boolean;
  managerId?: number | null;
  managerName?: string | null;
  createdBy: string;
  createdUtc: string;
  canDelete: boolean;
}

export interface UnbanResult {
  ip: string;
  results: UnbanPbx[];
  ignoreEntry?: IgnoreEntry | null;
  ignoreNote?: string | null;
  deploys: Deploy[];
}

export interface DeployState {
  pbxId: number;
  hostname: string;
  entries: number;
  deployed: boolean;
  inSync: boolean;
  appliedUtc?: string | null;
  appliedBy?: string | null;
  lastAttemptUtc?: string | null;
  lastError?: string | null;
}

export interface IgnoreList {
  entries: IgnoreEntry[];
  pbxs: DeployState[];
  isAdmin: boolean;
  /** Entries of other managers (managers only). */
  hiddenEntries: number;
}

export interface IgnoreChange {
  entry?: IgnoreEntry | null;
  deploys: Deploy[];
}

export interface LiveIgnore {
  pbxId: number;
  hostname: string;
  ok: boolean;
  error?: string | null;
  jails: { jail: string; entries: string[] }[];
  onPbxNotInList: string[];
  inListNotOnPbx: string[];
}

export interface BanAction {
  pbxBanActionId: number;
  pbxId?: number | null;
  hostname?: string | null;
  action: string;
  target: string;
  detail?: string | null;
  byKind: string;
  byName: string;
  createdUtc: string;
}

const base = "/api/ippbx/banlist";

function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === "" || v === false) continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const pbxBanlistApi = {
  list: (refresh: boolean, signal?: AbortSignal) => http<Banlist>(`${base}${qs({ refresh })}`, { signal }),
  lookup: (ip: string) => http<IpLookup>(`${base}/lookup${qs({ ip })}`),
  log: (p: { pbxId: number; tool: LogTool; target: string; all?: boolean; summary?: boolean }) => http<ToolOutput>(`${base}/log${qs(p)}`),
  unban: (body: { ip: string; pbxId?: number | null; ignore: boolean; comment?: string }) =>
    http<UnbanResult>(`${base}/unban`, { method: "POST", body: JSON.stringify(body) }),
  ignore: () => http<IgnoreList>(`${base}/ignore`),
  addIgnore: (body: { cidr: string; comment: string; pbxId?: number | null; force?: boolean }) =>
    http<IgnoreChange>(`${base}/ignore`, { method: "POST", body: JSON.stringify(body) }),
  deleteIgnore: (id: number) => http<IgnoreChange>(`${base}/ignore/${id}`, { method: "DELETE" }),
  deploy: (pbxId: number | null) => http<Deploy[]>(`${base}/ignore/deploy`, { method: "POST", body: JSON.stringify({ pbxId }) }),
  liveIgnore: () => http<LiveIgnore[]>(`${base}/ignore/live`),
  actions: (take = 200) => http<BanAction[]>(`${base}/actions${qs({ take })}`),
};

/** Readable text for the API's error codes. */
export function banlistError(e: unknown): string {
  const code = e instanceof Error ? e.message : String(e);
  switch (code) {
    case "invalid_ip": return "That is not a valid IP address.";
    case "invalid_cidr": return "That is not a valid IP address or network (e.g. 1.2.3.4 or 1.2.3.0/24).";
    case "invalid_username": return "That is not a valid PBX username.";
    case "ip_not_in_scope": return "This address is not related to any of your extensions.";
    case "username_not_in_scope": return "This username is not one of your extensions.";
    case "network_too_wide": return "Networks wider than /24 (IPv6: /64) cannot be ignored.";
    case "network_wide_needs_force": return "Networks wider than /24 (IPv6: /64) need “Allow a wide network”.";
    case "already_covered": return "The address or network is already on the ignore list.";
    case "comment_required": return "A comment is required.";
    case "entry_is_system": return "Infrastructure entries cannot be removed here.";
    case "pbx_not_found": return "That PBX is not available for the banlist.";
    default: return code || "Request failed";
  }
}
